/**
 * THE CABINET'S DISPLAY LANGUAGE: CHOSEN, KEPT IN THE BROWSER, AND NEVER WRITTEN TO THE PROFILE
 * (design 29, section 1b; the owner, 19 October: "from profile we can at max read first time").
 *
 * It is a setting of this browser, like the theme: kept in `localStorage`, and nothing is sent to
 * the server. The profile's `languageCode` is what the platform WRITES to the person in (push,
 * e-mail); a casual click here must not change it, so it is only ever read.
 *
 * On opening, the language is the first of these that exists:
 *   1. the choice saved in this browser;
 *   2. after sign-in, when nothing is saved: the profile's `languageCode`, read once and then saved,
 *      so the person who never chose here starts in the language they already gave (`adoptProfileLanguage`);
 *   3. the browser's own language, when it is one of the fourteen;
 *   4. English.
 *
 * Every read and write of storage is guarded: a private window or a blocked site-data setting throws,
 * and the cabinet then simply forgets the choice at the next load.
 */
import {
  DEFAULT_CABINET_LANGUAGE,
  cabinetLanguageOf,
  changeCabinetLanguage,
  i18n,
  isCabinetLanguage,
} from '../../i18n';
import type { CabinetLanguage } from '../../i18n';

/** Where the choice is kept. */
export const CABINET_LANGUAGE_KEY = 'moonx-cabinet-language';

/** How long the first paint waits for a chosen language's files before drawing in English. */
const FIRST_PAINT_WAIT_MS = 1500;

/** The language saved in this browser, or `null`: none, not one of the fourteen, or unreadable. */
export function savedCabinetLanguage(): CabinetLanguage | null {
  try {
    const saved = window.localStorage.getItem(CABINET_LANGUAGE_KEY);
    return isCabinetLanguage(saved) ? saved : null;
  } catch {
    return null;
  }
}

function saveCabinetLanguage(language: CabinetLanguage): void {
  try {
    window.localStorage.setItem(CABINET_LANGUAGE_KEY, language);
  } catch {
    // Storage refused: the choice holds in this tab and is forgotten at the next load.
  }
}

/** The first of the browser's own languages that is one of the fourteen, or `null`. */
export function browserCabinetLanguage(
  languages: readonly string[] = navigator.languages,
): CabinetLanguage | null {
  for (const tag of languages) {
    const language = cabinetLanguageOf(tag);
    if (language !== null) {
      return language;
    }
  }
  return null;
}

/** The language to open in before anybody is signed in: steps 1, 3 and 4 above. */
export function startingCabinetLanguage(
  saved: CabinetLanguage | null = savedCabinetLanguage(),
  browser: CabinetLanguage | null = browserCabinetLanguage(),
): CabinetLanguage {
  return saved ?? browser ?? DEFAULT_CABINET_LANGUAGE;
}

/** The author chose `language`: every screen switches, and the choice is kept in this browser. */
export async function chooseCabinetLanguage(language: CabinetLanguage): Promise<void> {
  // Saved first, so the other tabs' `storage` event finds the value they are told about.
  saveCabinetLanguage(language);
  await changeCabinetLanguage(language);
}

/**
 * Step 2: the profile's `languageCode`, adopted only while this browser has no choice of its own -
 * and then saved, so the profile is not asked again and a later choice here always wins. A code
 * that is not one of the fourteen changes nothing.
 */
export async function adoptProfileLanguage(languageCode: string | null | undefined): Promise<void> {
  const language = cabinetLanguageOf(languageCode);
  if (language === null || savedCabinetLanguage() !== null) {
    return;
  }
  await chooseCabinetLanguage(language);
}

/**
 * Other open tabs follow a choice made in one of them, by the rule the theme keeps since v1.0.17
 * (`crossTabColorSchemeManager`, web/ui/src/AppProviders.tsx): in Safari two tabs can share a web
 * process, so an event can arrive after storage has moved on. Only an event that matches what
 * storage holds NOW is followed, and following it writes nothing back. Returns the unsubscribe.
 */
export function followOtherTabs(): () => void {
  const onStorage = (event: StorageEvent): void => {
    if (event.key !== CABINET_LANGUAGE_KEY) {
      return;
    }
    const stored = savedCabinetLanguage();
    if (stored !== null && stored === event.newValue && stored !== i18n.language) {
      void changeCabinetLanguage(stored);
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener('storage', onStorage);
  };
}

/** `<html lang>` says the language the page is in, so a screen reader reads it in that voice. */
export function followDocumentLanguage(): () => void {
  const apply = (language: string): void => {
    document.documentElement.lang = language;
  };
  apply(i18n.language);
  i18n.on('languageChanged', apply);
  return () => {
    i18n.off('languageChanged', apply);
  };
}

/**
 * The start, from `main.tsx`: `<html lang>` and the other tabs followed, and the starting language
 * loaded. It resolves once that language's files are in - so the first paint is already in it - or
 * after a short wait, so a stalled download leaves the cabinet in English rather than blank.
 */
export async function startCabinetLanguage(): Promise<void> {
  followDocumentLanguage();
  followOtherTabs();
  const loaded = changeCabinetLanguage(startingCabinetLanguage());
  await Promise.race([
    loaded,
    new Promise<void>((resolve) => {
      setTimeout(resolve, FIRST_PAINT_WAIT_MS);
    }),
  ]);
}
